/**
 * External shop cart (external_cart mode) — the widget performs cart operations
 * ITSELF via a same-origin fetch to the shop's endpoint, in the visitor's own
 * session (cookies flow automatically; no CORS for same-origin).
 *
 * Contract (implemented by the shop): POST JSON
 *   { op: 'add'|'remove'|'clear'|'view', items: [{ sku, qty }] }
 * → { status: 'ok'|'error', result?: 'done'|'rejected', cart?: { count, total_cents,
 *     currency, items? }, message? }
 *
 * `result` is a business-level outcome layered ON TOP of the protocol `status`
 * (which stays 'ok' for a rejection — the request itself succeeded, the shop
 * just declined the mutation, e.g. "produkt X niedostępny"). Absent/null/'done'
 * = the op went through — a typed-DTO shop (e.g. Go without `omitempty`)
 * serializes "no rejection" as a literal `null`, and the server accepts that
 * as done too, so the client must match. `result` only applies to mutations
 * (add/remove/clear); op 'view' has no rejection concept and its `result`
 * (if any) is IGNORED.
 *
 * `cart.items` (op 'view'): ABSENT = shop doesn't expose cart contents (older
 * integrations keep working); [] = cart is empty. The distinction is meaningful
 * and must survive every hop.
 *
 * Only a literal `status: 'ok'` counts as protocol success (determinism rule
 * R.1 — no ambiguous success; the transcript chip must never overclaim).
 * R.1 extends to `result`: for a mutation, anything other than absent/null/
 * 'done'/'rejected' collapses to `status: 'error'` (protocol violation, not a
 * business rejection) — same "no ambiguous outcome" guarantee, one level up.
 *
 * Once-guard: an op executes AT MOST ONCE per browser — the guard key is set
 * BEFORE the fetch, so history refetch / reload never re-adds items. The server
 * status is the first line of dedup; this guard covers the pending window.
 */

import { BRAND_SLUG } from '../brand.js';

export type ShopCartOpPayload = {
  op: string;
  items: { sku: string; qty: number }[];
};

export type ShopCartSnapshotItem = {
  sku: string;
  name?: string;
  qty?: number;
  line_total_cents?: number | null;
};

export type ShopCartSnapshot = {
  count?: number;
  total_cents?: number | null;
  currency?: string;
  items?: ShopCartSnapshotItem[];
};

/**
 * Internal outcome of one shop-cart op — NOT the wire shape (see `toEventOutcome`
 * for the wire mapping). 'rejected' is a business decline (wire `status:'ok',
 * result:'rejected'`); 'error' covers HTTP failures, network errors, and any
 * protocol violation (missing/unrecognized `status` or `result`).
 */
export type ShopCartResult = {
  status: 'ok' | 'rejected' | 'error';
  cart?: ShopCartSnapshot;
  message?: string;
};

/** Wire-shape triple the widget POSTs back to our API to report an op's outcome. */
export type CartEventOutcome = {
  status: 'ok' | 'error';
  result?: 'rejected';
  message?: string;
};

/** Raw shop JSON body — fields are `unknown`-typed pending validation below. */
type ShopCartWireBody = {
  status?: unknown;
  result?: unknown;
  cart?: unknown;
  message?: unknown;
};

const MAX_MESSAGE_LEN = 200;
const MAX_EVENT_MESSAGE_LEN = 500;

/**
 * Cosmetic client-side twin of the server's canonical shop-message sanitizer —
 * used ONLY at DISPLAY points (the rejected chip, the card-path banner/
 * breadcrumb), never on the wire: strip tags, fold control chars (incl.
 * \n\r\t) to a space, collapse whitespace, trim, cap at MAX_MESSAGE_LEN
 * (counted in Unicode code points — a UTF-16-unit slice can split a surrogate
 * pair at the boundary and produce a lone escape that breaks JSON parsing
 * downstream). Only strings are accepted; anything else (including an empty
 * result after sanitizing) → undefined. Idempotent, so replaying an
 * already-server-sanitized message is a no-op.
 */
export function sanitizeShopMessage(raw: unknown): string | undefined {
  if (typeof raw !== 'string') {
    return undefined;
  }
  const collapsed = raw
    .replace(/<[^>]*>/g, '')
    // Intentional: strip C0 controls + DEL (incl. \n\r\t) — collapse to a space.
    .replace(/[\x00-\x1F\x7F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (collapsed === '') {
    return undefined;
  }
  return [...collapsed].slice(0, MAX_MESSAGE_LEN).join('');
}

/**
 * Map an internal `ShopCartResult` to the wire triple the widget reports back
 * to our API. Used by BOTH the LLM-driven op path and the product-card path so
 * the event POST is identical regardless of trigger.
 *
 * `message` travels RAW (not sanitized) on the wire for both 'rejected' and
 * 'error' — the server is the canonical sanitizer for owner diagnostics
 * (`shop_message`/`client_message`); double-sanitizing here would produce
 * artifacts where the client regex and the server's strip_tags disagree (e.g.
 * an unclosed '<'). Display-time rendering sanitizes separately via
 * `sanitizeShopMessage`.
 */
export function toEventOutcome(r: ShopCartResult): CartEventOutcome {
  if (r.status === 'ok') {
    return { status: 'ok' };
  }
  if (r.status === 'rejected') {
    return { status: 'ok', result: 'rejected', message: r.message?.slice(0, MAX_EVENT_MESSAGE_LEN) };
  }
  return { status: 'error', message: r.message?.slice(0, MAX_EVENT_MESSAGE_LEN) };
}

/** Max cart items forwarded from a shop response — mirror of the server cap. */
const MAX_SNAPSHOT_ITEMS = 50;

/**
 * Coerce a shop-provided cart object into a safe snapshot: items only when the
 * shop actually sent an array (absence is meaningful — "view unsupported"),
 * clamped to MAX_SNAPSHOT_ITEMS, sku coerced to string.
 */
export function coerceShopCart(cart: unknown): ShopCartSnapshot | undefined {
  if (typeof cart !== 'object' || cart === null) {
    return undefined;
  }
  const raw = cart as Record<string, unknown>;
  const out: ShopCartSnapshot = {
    count: typeof raw.count === 'number' ? raw.count : undefined,
    total_cents: typeof raw.total_cents === 'number' ? raw.total_cents : undefined,
    currency: typeof raw.currency === 'string' ? raw.currency : undefined,
  };
  if (Array.isArray(raw.items)) {
    out.items = raw.items
      .filter((i): i is Record<string, unknown> => typeof i === 'object' && i !== null)
      .map((i) => ({
        sku: String(i.sku ?? '').trim(),
        name: typeof i.name === 'string' ? i.name.slice(0, 255) : undefined,
        qty: typeof i.qty === 'number' ? i.qty : undefined,
        line_total_cents: typeof i.line_total_cents === 'number' ? i.line_total_cents : undefined,
      }))
      // Pozycje bez sku (usługa/wysyłka spoza feedu) pomijamy wszędzie tak samo:
      // karta i event widzą identyczną listę, a serwer nie ma czego odrzucać.
      .filter((i) => i.sku !== '')
      .slice(0, MAX_SNAPSHOT_ITEMS);
  }
  return out;
}

const FETCH_TIMEOUT_MS = 10_000;

/**
 * Resolve the configured endpoint against the host page's origin. Root-relative
 * paths ('/kobot-cart') are inherently same-origin on every page; absolute https
 * URLs were validated against allowed_origins server-side at save time.
 * Protocol-relative ('//host') is rejected defensively (cross-origin escape).
 */
export function resolveShopEndpoint(endpointUrl: string, origin: string): string | null {
  const trimmed = endpointUrl.trim();
  if (trimmed === '' || trimmed.startsWith('//')) {
    return null;
  }
  if (trimmed.startsWith('/')) {
    return `${origin}${trimmed}`;
  }
  if (trimmed.startsWith('https://')) {
    return trimmed;
  }
  return null;
}

/**
 * Execute one shop-cart operation. Never throws — every failure mode collapses
 * to { status: 'error' } so the caller renders an honest chip.
 */
export async function executeShopCartOp(
  endpointUrl: string,
  payload: ShopCartOpPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<ShopCartResult> {
  const endpoint = resolveShopEndpoint(endpointUrl, window.location.origin);
  if (endpoint === null) {
    return { status: 'error' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      // Same-origin: cookies odwiedzającego idą domyślnie. `same-origin` mode
      // twardo blokuje przypadkowy cross-origin (np. zły config po stronie serwera).
      mode: 'same-origin',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!response.ok) {
      return { status: 'error' };
    }
    const body = (await response.json().catch(() => null)) as ShopCartWireBody | null;
    if (body === null || body.status !== 'ok') {
      return { status: 'error', message: typeof body?.message === 'string' ? body.message : undefined };
    }
    // 'view' has no rejection concept — `result` (if the shop sends one anyway)
    // is ignored entirely, preserving today's behavior.
    if (payload.op === 'view') {
      return { status: 'ok', cart: coerceShopCart(body.cart) };
    }
    // Loose null check (`== null`): a shop with a typed DTO (e.g. Go without
    // `omitempty`) serializes "no rejection" as a literal `null`, not an
    // absent key — both mean "the mutation went through" server-side too
    // (validated `nullable`), so both must resolve here, not fall through to
    // status:'error' on a successful add.
    if (body.result == null || body.result === 'done') {
      return { status: 'ok', cart: coerceShopCart(body.cart) };
    }
    if (body.result === 'rejected') {
      // RAW pass-through (trimmed, capped at the wire limit) — NOT sanitized
      // here. Sanitization is a display concern (see `sanitizeShopMessage`);
      // the wire message stays raw so the server's canonical sanitizer sees
      // the shop's original text for owner diagnostics.
      const trimmed = typeof body.message === 'string' ? body.message.trim().slice(0, MAX_EVENT_MESSAGE_LEN) : undefined;
      return { status: 'rejected', message: trimmed === '' ? undefined : trimmed };
    }
    // Unrecognized `result` value — R.1 extension: not an ambiguous success.
    return { status: 'error' };
  } catch {
    return { status: 'error' };
  } finally {
    clearTimeout(timer);
  }
}

/** Guard entries older than this are swept — conversations expire after 24h,
 *  so a 48h-old claim can never guard a still-pending op. Keeps localStorage
 *  from accumulating one key per invocation forever. */
const CLAIM_TTL_MS = 48 * 60 * 60 * 1000;

/**
 * At-most-once guard for a pending op. Returns true when this browser has NOT
 * yet attempted the op AND atomically marks it as attempted (mark-before-fetch).
 * Storage unavailable → allow (widget still works; server status dedups repeats).
 */
export function claimCartOpOnce(widgetKey: string, invocationId: string, storage?: Storage | null): boolean {
  const store = storage !== undefined ? storage : safeLocalStorage();
  if (store === null) {
    return true;
  }
  const prefix = `${BRAND_SLUG}:${widgetKey}:cartop:`;
  const key = `${prefix}${invocationId}`;
  try {
    sweepStaleClaims(store, prefix);
    if (store.getItem(key) !== null) {
      return false;
    }
    store.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

function sweepStaleClaims(store: Storage, prefix: string): void {
  const cutoff = Date.now() - CLAIM_TTL_MS;
  const stale: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k !== null && k.startsWith(prefix) && Number(store.getItem(k)) < cutoff) {
      stale.push(k);
    }
  }
  for (const k of stale) {
    store.removeItem(k);
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
